/**
 * CreateMeetingDialog Component
 * Dialog for creating Google Meet links
 */
import { useState, useEffect } from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "./ui/dialog";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Textarea } from "./ui/textarea";
import { Badge } from "./ui/badge";
import { Video, Calendar, Clock, Users, Link2, Copy, Check, ExternalLink, Loader2, AlertCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";

const API = process.env.REACT_APP_BACKEND_URL;

export const CreateMeetingDialog = ({
  open,
  onOpenChange,
  token,
  onMeetingCreated,
}) => {
  const [loading, setLoading] = useState(false);
  const [calendarStatus, setCalendarStatus] = useState({ connected: false, loading: true });
  const [copied, setCopied] = useState(false);
  const [createdMeeting, setCreatedMeeting] = useState(null);
  
  const [formData, setFormData] = useState({
    title: "",
    description: "",
    date: "",
    startTime: "",
    endTime: "",
    attendeeEmails: "",
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
  });

  // Check Google Calendar connection status
  useEffect(() => {
    const checkStatus = async () => {
      if (!token) return;
      
      try {
        const response = await axios.get(`${API}/google/calendar/status`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        setCalendarStatus({ connected: response.data.connected, loading: false, email: response.data.google_email });
      } catch (error) {
        setCalendarStatus({ connected: false, loading: false });
      }
    };
    
    if (open) {
      checkStatus();
      setCreatedMeeting(null);
    }
  }, [open, token]);

  const handleConnectCalendar = async () => {
    try {
      const response = await axios.get(`${API}/google/calendar/connect`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      
      // Open Google OAuth in new window
      window.open(response.data.authorization_url, "_blank", "width=500,height=600");
      toast.info("Complete the Google sign-in in the new window");
    } catch (error) {
      toast.error("Failed to start Google Calendar connection");
    }
  };

  const handleCreateMeeting = async () => {
    if (!formData.title || !formData.date || !formData.startTime || !formData.endTime) {
      toast.error("Please fill in all required fields");
      return;
    }

    setLoading(true);
    
    try {
      // Format date/time to ISO 8601
      const startDateTime = `${formData.date}T${formData.startTime}:00`;
      const endDateTime = `${formData.date}T${formData.endTime}:00`;
      
      // Parse attendee emails
      const attendeeEmails = formData.attendeeEmails
        .split(/[,;\n]/)
        .map(e => e.trim())
        .filter(e => e && e.includes("@"));

      const response = await axios.post(
        `${API}/google/meet/create`,
        {
          title: formData.title,
          description: formData.description,
          start_time: startDateTime,
          end_time: endDateTime,
          attendee_emails: attendeeEmails,
          timezone: formData.timezone,
        },
        { headers: { Authorization: `Bearer ${token}` } }
      );

      setCreatedMeeting(response.data);
      toast.success("Google Meet link created!");
      onMeetingCreated?.(response.data);
      
    } catch (error) {
      console.error("Create meeting error:", error);
      toast.error(error.response?.data?.detail || "Failed to create meeting");
    } finally {
      setLoading(false);
    }
  };

  const copyMeetLink = () => {
    if (createdMeeting?.meet_link) {
      navigator.clipboard.writeText(createdMeeting.meet_link);
      setCopied(true);
      toast.success("Meet link copied to clipboard!");
      setTimeout(() => setCopied(false), 2000);
    }
  };

  const resetForm = () => {
    setFormData({
      title: "",
      description: "",
      date: "",
      startTime: "",
      endTime: "",
      attendeeEmails: "",
      timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    });
    setCreatedMeeting(null);
  };

  return (
    <Dialog open={open} onOpenChange={(isOpen) => {
      if (!isOpen) resetForm();
      onOpenChange(isOpen);
    }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Video className="w-5 h-5 text-blue-600" />
            Create Google Meet
          </DialogTitle>
          <DialogDescription>
            Schedule a virtual meeting with a Google Meet link
          </DialogDescription>
        </DialogHeader>

        {calendarStatus.loading ? (
          <div className="py-8 text-center">
            <Loader2 className="w-6 h-6 animate-spin mx-auto mb-2 text-slate-400" />
            <p className="text-sm text-slate-500">Checking Google Calendar connection...</p>
          </div>
        ) : !calendarStatus.connected ? (
          <div className="py-8 text-center">
            <div className="w-16 h-16 bg-slate-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Calendar className="w-8 h-8 text-slate-400" />
            </div>
            <h3 className="font-medium text-slate-900 mb-2">Connect Google Calendar</h3>
            <p className="text-sm text-slate-500 mb-4">
              Connect your Google account to create Meet links and sync events to your calendar.
            </p>
            <Button onClick={handleConnectCalendar} className="bg-blue-600 hover:bg-blue-700">
              <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
                <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              Connect Google Account
            </Button>
          </div>
        ) : createdMeeting ? (
          // Success state
          <div className="py-6">
            <div className="text-center mb-6">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="w-8 h-8 text-green-600" />
              </div>
              <h3 className="font-semibold text-slate-900 text-lg">{createdMeeting.title}</h3>
              <p className="text-sm text-slate-500 mt-1">Meeting created successfully!</p>
            </div>
            
            <div className="bg-slate-50 rounded-lg p-4 space-y-3">
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Meet Link:</span>
                <div className="flex items-center gap-2">
                  <a 
                    href={createdMeeting.meet_link} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                  >
                    Join Meeting <ExternalLink className="w-3 h-3" />
                  </a>
                  <Button size="sm" variant="ghost" onClick={copyMeetLink}>
                    {copied ? <Check className="w-4 h-4 text-green-600" /> : <Copy className="w-4 h-4" />}
                  </Button>
                </div>
              </div>
              
              <div className="flex items-center justify-between">
                <span className="text-sm text-slate-600">Calendar Event:</span>
                <a 
                  href={createdMeeting.html_link} 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline flex items-center gap-1"
                >
                  View in Calendar <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
            
            <div className="flex gap-3 mt-6">
              <Button variant="outline" className="flex-1" onClick={resetForm}>
                Create Another
              </Button>
              <Button className="flex-1" onClick={() => onOpenChange(false)}>
                Done
              </Button>
            </div>
          </div>
        ) : (
          // Form state
          <>
            <div className="space-y-4">
              <div className="flex items-center gap-2 text-sm text-slate-500 bg-slate-50 p-2 rounded-lg">
                <Check className="w-4 h-4 text-green-500" />
                Connected as {calendarStatus.email}
              </div>
              
              <div>
                <Label>Meeting Title *</Label>
                <Input
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="e.g., Parent Orientation Meeting"
                  className="mt-1"
                />
              </div>
              
              <div>
                <Label>Description</Label>
                <Textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  placeholder="Meeting agenda or notes..."
                  className="mt-1"
                  rows={2}
                />
              </div>
              
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <Label>Date *</Label>
                  <Input
                    type="date"
                    value={formData.date}
                    onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                    className="mt-1"
                    min={new Date().toISOString().split("T")[0]}
                  />
                </div>
                <div>
                  <Label>Start Time *</Label>
                  <Input
                    type="time"
                    value={formData.startTime}
                    onChange={(e) => setFormData({ ...formData, startTime: e.target.value })}
                    className="mt-1"
                  />
                </div>
                <div>
                  <Label>End Time *</Label>
                  <Input
                    type="time"
                    value={formData.endTime}
                    onChange={(e) => setFormData({ ...formData, endTime: e.target.value })}
                    className="mt-1"
                  />
                </div>
              </div>
              
              <div>
                <Label className="flex items-center gap-2">
                  <Users className="w-4 h-4" />
                  Invite Attendees
                </Label>
                <Textarea
                  value={formData.attendeeEmails}
                  onChange={(e) => setFormData({ ...formData, attendeeEmails: e.target.value })}
                  placeholder="Enter email addresses (one per line or separated by commas)"
                  className="mt-1"
                  rows={2}
                />
                <p className="text-xs text-slate-500 mt-1">
                  Attendees will receive a calendar invitation with the Meet link
                </p>
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button 
                onClick={handleCreateMeeting} 
                disabled={loading}
                className="bg-blue-600 hover:bg-blue-700"
              >
                {loading ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Creating...
                  </>
                ) : (
                  <>
                    <Video className="w-4 h-4 mr-2" />
                    Create Meeting
                  </>
                )}
              </Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default CreateMeetingDialog;
